import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, BarChart3, Target, Handshake, Flame } from "lucide-react";
import { useTournament } from "../context/TournamentContext";
import { useLanguage } from "../context/LanguageContext";
import LanguageSelector from "../components/LanguageSelector";
import { computePlayerStats } from "../lib/playerStats";

// Reusable ranked list. metric: 'goals' | 'assists' | 'ga'
export function StatLeaderList({ title, subtitle, icon: Icon, players, metric, color, testid }) {
  const { t } = useLanguage();
  const valueOf = (p) => (metric === "goals" ? p.goals : metric === "assists" ? p.assists : p.goals + p.assists);
  const ranked = (players || []).filter((p) => valueOf(p) > 0).slice(0, 20);

  return (
    <div className="card-surface p-4 sm:p-5" data-testid={testid}>
      <div className={`flex items-center gap-2 mb-1 ${color}`}>
        <Icon className="w-5 h-5" />
        <h3 className="font-heading uppercase tracking-wide text-base text-white">{title}</h3>
      </div>
      {subtitle && <p className="text-white/30 text-xs mb-3">{subtitle}</p>}
      {ranked.length === 0 ? (
        <p className="text-white/40 text-sm py-4">{t("stats.noStatsYet")}</p>
      ) : (
        <ol className="space-y-1.5 mt-2">
          {ranked.map((p, i) => (
            <li
              key={p.playerId}
              className="flex items-center gap-3 py-1.5 border-b border-white/5 last:border-0"
              data-testid={`${testid}-row-${i}`}
            >
              <span className={`font-data text-sm w-6 text-center ${i === 0 ? color : "text-white/30"}`}>{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-white font-data text-sm truncate">{p.playerName}</p>
                <p className="text-white/40 text-xs truncate">{p.teamName}</p>
              </div>
              <span className={`font-data font-bold text-lg ${color}`}>{valueOf(p)}</span>
              {metric === "ga" && (
                <span className="text-white/30 text-xs font-data whitespace-nowrap">
                  {p.goals}{t("stats.goalsAbbr")}/{p.assists}{t("stats.assistsAbbr")}
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function PlayerStatsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentTournament, loadTournament } = useTournament();
  const { t } = useLanguage();

  useEffect(() => {
    if (id) loadTournament(id);
  }, [id, loadTournament]);

  if (!currentTournament) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-white/60">{t("loading")}</div>
      </div>
    );
  }

  const backTo = currentTournament.format === "league" ? `/league/${id}` : `/bracket/${id}`;
  const { scorers, assisters, combined } = computePlayerStats(currentTournament);

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate(backTo)}
            data-testid="stats-back-btn"
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-heading uppercase text-sm hidden sm:inline">{t("back")}</span>
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <BarChart3 className="w-5 h-5 text-neon-purple flex-shrink-0" />
            <h1 className="font-heading text-base sm:text-xl uppercase text-white truncate">
              {t("stats.playerStats")}
            </h1>
          </div>
          <LanguageSelector />
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="player-stats-lists">
          <StatLeaderList
            title={t("stats.topScorers")}
            icon={Target}
            players={scorers}
            metric="goals"
            color="text-neon-cyan"
            testid="top-scorers-list"
          />
          <StatLeaderList
            title={t("stats.topAssisters")}
            icon={Handshake}
            players={assisters}
            metric="assists"
            color="text-neon-purple"
            testid="top-assisters-list"
          />
          <StatLeaderList
            title={t("stats.topGA")}
            subtitle={t("stats.topGADesc")}
            icon={Flame}
            players={combined}
            metric="ga"
            color="text-orange-400"
            testid="top-ga-list"
          />
        </div>
      </main>
    </div>
  );
}
